import { Button, Grid, Group, Modal } from '@mantine/core';
import { IconCopy, IconX } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import LockableButton from './Lockable/LockableButton';
import SearchableCombobox from './SearchableCombobox';

const CrossDomainCopy = ({
  resourceType,
  resourceId,
  basePath,
  fallbackAction,
  restrictionKey,
  disabled = false,
  children,
}) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { accountId, domainId } = useParams();

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [selectedDomainForCopy, setSelectedDomainForCopy] = useState(null);

  // Flatten options with account name prefix for SearchableCombobox
  const selectData = useMemo(() => {
    const accounts = user.userRoles.filter((role) => role.account);
    return accounts.flatMap((role) =>
      role.account.domains.map((domain) => ({
        value: JSON.stringify({ domain: domain.id, account: role.account.id }),
        label: accounts.length > 1 ? `${role.account.name} / ${domain.name}` : domain.name,
      }))
    );
  }, [user.userRoles]);

  const multipleDomainOptions = selectData.length > 1;

  const handleCopy = () => {
    if (!multipleDomainOptions) {
      // Use provided fallback action for single domain
      if (fallbackAction) {
        fallbackAction();
      }
      return;
    }
    // Set current domain as default selection
    setSelectedDomainForCopy(
      JSON.stringify({
        domain: parseInt(domainId),
        account: parseInt(accountId),
      })
    );
    setCopyModalOpen(true);
  };

  const handleCopySubmit = (e) => {
    e.preventDefault();
    if (selectedDomainForCopy) {
      const parsed = JSON.parse(selectedDomainForCopy);
      // Only include fromDomain if copying to a different domain
      const isCrossDomain = parsed.domain !== parseInt(domainId);
      const fromDomainParam = isCrossDomain ? `&fromDomain=${domainId}` : '';
      const targetPath = `/accounts/${parsed.account}/domains/${parsed.domain}/${basePath}/new?from=${resourceId}${fromDomainParam}`;
      navigate(targetPath);
      setCopyModalOpen(false);
      setSelectedDomainForCopy(null);
    }
  };

  const closeCopyModal = () => {
    setCopyModalOpen(false);
    setSelectedDomainForCopy(null);
  };

  return (
    <>
      <LockableButton restrictionKey={restrictionKey} color="cyan" onClick={handleCopy} disabled={disabled}>
        <IconCopy size={16} style={{ marginRight: 4 }} /> {children || 'Make a copy'}
      </LockableButton>

      <Modal opened={copyModalOpen} onClose={closeCopyModal} title={`Copy ${resourceType}`}>
        <form onSubmit={handleCopySubmit}>
          <Grid align="center">
            <Grid.Col span={4}>Target Domain</Grid.Col>
            <Grid.Col span={8}>
              <SearchableCombobox
                options={selectData}
                value={selectedDomainForCopy}
                onChange={setSelectedDomainForCopy}
                placeholder="Select target domain..."
              />
            </Grid.Col>
          </Grid>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeCopyModal} leftSection={<IconX size={16} />}>
              Close
            </Button>
            <Button type="submit" disabled={!selectedDomainForCopy} leftSection={<IconCopy size={16} />}>
              Make a copy
            </Button>
          </Group>
        </form>
      </Modal>
    </>
  );
};

export default CrossDomainCopy;
